import { useEffect, useState } from "react";
import { supabase } from "../auth";
import { listEventsForSubject } from "../db/gardenEvents";
import type { GardenEventRow, Json } from "@garden/types/db";
import "./PlantEventLogTab.css";

interface Props {
  plantId: string;
}

export function PlantEventLogTab({ plantId }: Props) {
  const [rows, setRows] = useState<GardenEventRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    listEventsForSubject(supabase, "plant", plantId)
      .then((data) => {
        if (!active) return;
        setRows(data);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      active = false;
    };
  }, [plantId]);

  if (error) {
    return <p className="LogTab__error">{error}</p>;
  }
  if (rows === null) {
    return <div className="LogTab__loading">Loading…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="LogTab">
        <p className="LogTab__empty">
          No events yet. Activity here is auto-logged from the rest of the app.
        </p>
        <p className="LogTab__appendOnly">Append-only.</p>
      </div>
    );
  }

  return (
    <div className="LogTab">
      <ul className="LogTab__list">
        {rows.map((ev) => (
          <li key={ev.id} className="LogTab__row">
            <span className="LogTab__dot" aria-hidden="true" />
            <div className="LogTab__body">
              <div className="LogTab__date">{formatDate(ev.event_date)}</div>
              <div className="LogTab__text">{describe(ev)}</div>
            </div>
          </li>
        ))}
      </ul>
      <p className="LogTab__appendOnly">Append-only — every change is recorded.</p>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function describe(ev: GardenEventRow): React.ReactNode {
  const details = (ev.details ?? {}) as { [k: string]: Json };
  switch (ev.event_type) {
    case "plant_created":
      return <><strong>Plant created.</strong> {asString(details["common_name"]) ?? "Added to the garden."}</>;
    case "status_changed":
      return (
        <>
          <strong>Status changed.</strong>{" "}
          {asString(details["from"]) ?? "?"} → {asString(details["to"]) ?? "?"}
        </>
      );
    case "observation_logged":
      return (
        <>
          <strong>Observation logged.</strong>
          {asString(details["kind"]) ? ` ${asString(details["kind"])}.` : ""}
        </>
      );
    case "task_completed":
      return (
        <>
          <strong>Task completed.</strong>{" "}
          {asString(details["task_name"]) ?? ""}
        </>
      );
    case "task_skipped":
      return (
        <>
          <strong>Task skipped.</strong>{" "}
          {asString(details["task_name"]) ?? ""}
        </>
      );
    case "photo_added":
      return <><strong>Photo added.</strong></>;
    default:
      return <strong>{ev.event_type.replace(/_/g, " ")}.</strong>;
  }
}

function asString(v: Json | undefined): string | null {
  if (typeof v === "string") return v;
  return null;
}
